import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { FolderKanban, Image as ImageIcon, Eye, DollarSign, Plus, ArrowUpRight, Heart, MessageCircle, Users } from "lucide-react";

const postImg = (pid) => `${API}/posts/${pid}/image`;

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [following, setFollowing] = useState([]);
  const [feed, setFeed] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, p, fol, fed] = await Promise.all([
          api.get("/analytics/overview"),
          api.get("/projects"),
          api.get("/me/following").catch(()=>({data:[]})),
          api.get("/me/following/feed").catch(()=>({data:[]})),
        ]);
        setStats(s.data); setProjects(p.data.slice(0, 6));
        setFollowing(fol.data.slice(0, 10)); setFeed(fed.data.slice(0, 6));
      } catch {}
    })();
  }, []);

  const cards = [
    { label: "Projects", value: stats?.projects ?? "—", icon: FolderKanban },
    { label: "Photos", value: stats?.photos ?? "—", icon: ImageIcon },
    { label: "Gallery views", value: stats?.gallery_views ?? "—", icon: Eye },
    { label: "Revenue", value: stats ? `$${stats.revenue.toFixed(2)}` : "—", icon: DollarSign },
  ];

  return (
    <Layout>
      <div className="mb-10">
        <p className="text-amber-400 text-sm tracking-widest uppercase mb-2">Welcome back</p>
        <h1 className="font-display text-4xl md:text-5xl">Hello, {user?.display_name?.split(" ")[0]}</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {cards.map(c => (
          <div key={c.label} data-testid={`stat-${c.label}`} className="p-5 rounded-md bg-stone-900/50 border border-white/5">
            <div className="flex items-center justify-between mb-3">
              <c.icon className="w-4 h-4 text-amber-400"/>
              <span className="text-xs text-stone-500 uppercase tracking-wider">{c.label}</span>
            </div>
            <div className="font-display text-3xl">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl">Recent projects</h2>
        <Button asChild data-testid="btn-new-project" className="bg-amber-500 text-stone-950 hover:bg-amber-400">
          <Link to="/projects"><Plus className="w-4 h-4 mr-2"/>New project</Link>
        </Button>
      </div>

      {projects.length === 0 ? (
        <div className="p-12 text-center rounded-md bg-stone-900/40 border border-dashed border-white/10 mb-10">
          <p className="text-stone-400 mb-4">No projects yet. Start your first shoot.</p>
          <Button asChild data-testid="btn-empty-create" className="bg-amber-500 text-stone-950 hover:bg-amber-400"><Link to="/projects">Create project</Link></Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {projects.map(p => (
            <Link key={p.project_id} to={`/projects/${p.project_id}`} data-testid={`project-card-${p.project_id}`}
              className="group p-5 rounded-md bg-stone-900/50 border border-white/5 hover:border-amber-500/30 transition-colors block">
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0">
                  <h3 className="font-medium truncate">{p.title}</h3>
                  <p className="text-xs text-stone-500 mt-1">{p.client_name || "No client"}</p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-stone-600 group-hover:text-amber-400" />
              </div>
              <div className="flex items-center gap-4 text-xs text-stone-500">
                <span>{p.photo_count} photos</span>
                <span className="capitalize">{p.status}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Following */}
      {following.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl flex items-center gap-2"><Users className="w-5 h-5 text-amber-400"/>Following</h2>
            <Link to="/discover" className="text-xs text-stone-500 hover:text-amber-400">Discover more →</Link>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 mb-8">
            {following.map(u => (
              <Link key={u.user_id} to={`/p/${u.profile_slug}`} className="flex flex-col items-center gap-2 shrink-0">
                {u.picture ? <img src={u.picture} className="w-14 h-14 rounded-full ring-2 ring-amber-500/50"/> : <div className="w-14 h-14 rounded-full bg-stone-800"/>}
                <span className="text-[11px] text-stone-400 max-w-[80px] truncate">{u.display_name?.split(" ")[0]}</span>
              </Link>
            ))}
          </div>
        </>
      )}

      {feed.length > 0 && (
        <>
          <h2 className="font-display text-2xl mb-4">From photographers you follow</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mb-10">
            {feed.map(p => (
              <Link key={p.post_id} to="/showcase" className="block rounded-md overflow-hidden bg-stone-900 border border-white/5 hover:border-amber-500/30 group">
                <img src={postImg(p.post_id)} alt="" className="w-full aspect-square object-cover"/>
                <div className="p-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    {p.author?.picture ? <img src={p.author.picture} className="w-6 h-6 rounded-full"/> : <div className="w-6 h-6 rounded-full bg-stone-800"/>}
                    <span className="text-xs truncate">{p.author?.display_name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-stone-500">
                    <span className="flex items-center gap-1"><Heart className="w-3 h-3"/>{p.like_count||0}</span>
                    <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3"/>{p.comment_count||0}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </Layout>
  );
}
